import { Chess } from 'chess.js'

// Parses PGN text into a flat mainline move list. Variations are stripped
// (this is a video-sync tool — only the game as played matters), comments and
// clock annotations are tolerated, NAGs ($1 etc.) are removed.
//
// Returns { headers, startFen, moves, error }.
// move = { ply, san, label, color, moveNumber, from, to, before, after }
// where before/after are full FENs (chess.js verbose history provides them).

export function parsePgn(text) {
  const base = { headers: {}, startFen: new Chess().fen(), moves: [], error: null }
  if (typeof text !== 'string' || !text.trim()) {
    return { ...base, error: 'PGN is empty — paste a game or upload a .pgn file.' }
  }

  const chess = new Chess()
  try {
    chess.loadPgn(sanitize(text), { strict: false })
  } catch (err) {
    return { ...base, error: `Could not parse PGN: ${err?.message ?? err}` }
  }

  const headers = cleanHeaders(chess.getHeaders())
  const verbose = chess.history({ verbose: true })
  if (verbose.length === 0) {
    const startFen = headers.FEN ?? base.startFen
    return { ...base, headers, startFen, error: 'The PGN contains no moves.' }
  }

  const startFen = verbose[0].before
  const moves = verbose.map((m, i) => {
    const moveNumber = moveNumberFromFen(m.before)
    return {
      ply: i + 1,
      san: m.san,
      color: m.color,
      moveNumber,
      label: m.color === 'w' ? `${moveNumber}. ${m.san}` : `${moveNumber}… ${m.san}`,
      from: m.from,
      to: m.to,
      before: m.before,
      after: m.after,
    }
  })

  return { headers, startFen, moves, error: null }
}

// Removes variations (recursively) and NAGs while preserving {comments}.
function sanitize(text) {
  let out = ''
  let depth = 0
  let inComment = false
  for (const ch of text) {
    if (inComment) {
      if (depth === 0) out += ch
      if (ch === '}') inComment = false
      continue
    }
    if (ch === '{') {
      inComment = true
      if (depth === 0) out += ch
      continue
    }
    if (ch === '(') {
      depth++
      continue
    }
    if (ch === ')') {
      if (depth > 0) depth--
      else out += ch
      continue
    }
    if (depth === 0) out += ch
  }
  // Strip NAGs outside comments (comments are matched first and kept).
  return out.replace(/\{[^}]*\}|\$\d+/g, (m) => (m[0] === '{' ? m : ''))
}

function moveNumberFromFen(fen) {
  const n = parseInt(String(fen).trim().split(/\s+/)[5], 10)
  return Number.isFinite(n) ? n : 1
}

function cleanHeaders(headers) {
  const out = {}
  for (const [key, value] of Object.entries(headers ?? {})) {
    if (value != null && value !== '') out[key] = value
  }
  return out
}
